import { defineComponent, nextTick, ref, type PropType } from 'vue';

import { Gender } from '@/constants/Gender';
import { PersonType } from '@/constants/PersonType';
import type { ClientRequest } from '@/features/Clients/Client.dto';
import type { ICase } from '@/interfaces';
import ClientFormSchema from '@/schemas/RenterFormSchema';
import { PublicService } from '@/services/publicService';
import { NForm, NFormItem, NFormItemGi, NGrid, NRadio, NRadioGroup, NSelect } from 'naive-ui';
import { AppInputCnpj, AppInputCpf, AppInputMoney } from '../Inputs';
import ContactForm from './ContactForm';
import { defineForm } from './FormBase';

export interface IRenterForm {
	validate(): Promise<unknown>;
}

export interface IContactForm {
	validate(): Promise<unknown>;
}

export const RenterForm = defineComponent({
	name: 'RenterForm',
	props: {
		showLegalPerson: {
			type: Boolean,
			default: true,
		},

		case: {
			type: Object as PropType<ICase>,
			default: null,
			required: false,
		},

		formSlot: {
			type: String, // 'renterA', 'renterB', etc.
			required: false,
		},
	},
	
	setup(props) {
		const initialEmail = ref((props.modelValue as any)?.contact?.email);
		const initialPhone = ref((props.modelValue as any)?.contact?.phone);

		return {
			initialEmail,
			initialPhone,
			phoneType: ref<'cel' | 'tel'>('cel'),
		};
	},

	extends: defineForm<ClientRequest>(),

	computed: {
		blockDuplicates() {
			const result = {
				documents: new Array<string>(),
				emails: new Array<string>(),
				phones: new Array<string>(),
			};

			if (this.case && this.case.quotation) {
				const renters = [
					this.case.quotation.renterA,
					this.case.quotation.renterB,
					this.case.quotation.renterC,
					this.case.quotation.renterD,
				];

				renters.forEach((r) => {
					// Pula o próprio slot por igualdade de referência
					if (this.formSlot && this.case?.quotation && (this.case.quotation as any)[this.formSlot] === r) return;

					if (r) {
						if (r.document) result.documents.push(r.document);
						if (r.contact?.email) result.emails.push(r.contact.email);
						if (r.contact?.phone) result.phones.push(r.contact.phone);
					}
				});
			}

			return result;
		},

		requestingLegalPerson() {
			const jobs = ['Empresário'];
			return (
				jobs.includes(this.formData.occupation) ||
				this.formData.personType == PersonType.LegalPerson
			);
		},
	},

	watch: {
		showLegalPerson: {
			immediate: true,
			handler(newValue: boolean) {
				if (!newValue && this.formData.personType !== PersonType.Natural) {
					this.formData.personType = PersonType.Natural;
				}
			},
		},
	},

	methods: {
		async searchRenter() {
			// Não buscar/autofillar um documento que já está na proposta: a validação de duplicado
			// (NoDuplicatesNormalized) já orienta o usuário, e assim evitamos puxar/sobrescrever dados.
			const digits = (this.formData.document || '').replace(/\D/g, '');
			if (!digits || this.blockDuplicates.documents.some((d) => (d || '').replace(/\D/g, '') === digits))
				return;

			// Busca pela identidade (Document) apenas. Não enviar o documento como cnpj: um
			// corresponsável Empresário (PF) compartilha o CNPJ da empresa do RenterA, e casar por
			// esse CNPJ traria/sobrescreveria o registro do RenterA (PJ).
			const response = await PublicService.getRenter(this.formData.document);
			const data = response.data as any;
			if (!data) return;
			nextTick(() => {
				if (!this.showLegalPerson && data.personType === PersonType.LegalPerson) {
					// Corresponsável nunca pode ser PJ: se o documento resolveu para um Renter PJ,
					// convertemos para PF Empresário — mantém o CNPJ como associação e o name (que num
					// PJ é o nome do responsável), força personType=Natural e descarta o que é exclusivo
					// do PJ (fantasyName = razão social) e o id vazado, para não reusar/sobrescrever o
					// registro PJ no envio.
					this.formData = {
						...data,
						id: undefined,
						personType: PersonType.Natural,
						occupation: 'Empresário',
						fantasyName: '',
					};
				} else {
					this.formData = data;
				}
				this.initialEmail = this.formData.contact?.email;
				this.initialPhone = this.formData.contact?.phone;
				this.$emit('update:modelValue', this.formData);
			});
		},
	},

	render() {
		return (
			<NForm
				size="large"
				ref="formRef"
				{...{ attrs: this.$attrs }}
				label-placement="top"
				rules={ClientFormSchema(this.formData, this.blockDuplicates)}
				model={this.formData}
				onSubmit={this.handleSubmit}
			>
				<div class="mt-2">
					{this.showLegalPerson && (
						<NFormItem label="Tipo de pessoa" path="personType">
							<NRadioGroup v-model:value={this.formData.personType}>
								<NRadio label="Pessoa física" value={PersonType.Natural} />

								<NRadio label="Pessoa jurídica" value={PersonType.LegalPerson} />
							</NRadioGroup>
						</NFormItem>
					)}

					<NGrid cols="1 400:2" x-gap="4" y-gap="4">
						<NFormItemGi label="CPF" path="document">
							<AppInputCpf
								data-cy="input-document"
								onChange={this.searchRenter}
								v-model={this.formData.document}
							/>
						</NFormItemGi>

						<NFormItemGi label="Renda declarada" path="declaredIncome">
							<AppInputMoney
								data-cy="input-declaredIncome"
								v-model={this.formData.declaredIncome}
							/>
						</NFormItemGi>

						<NFormItemGi label="Ocupação" path="occupation">
							<NSelect
								v-model:value={this.formData.occupation}
								options={[
									{ label: 'Assalariado', value: 'Assalariado' },
									{
										label: 'Autonomo/Profissional liberal',
										value: 'Autonomo/Profissional liberal',
									},
									{ label: 'Empresário', value: 'Empresário' },
									{ label: 'Aposentado', value: 'Aposentado' },
								]}
							/>
						</NFormItemGi>

						{this.formData.personType != PersonType.LegalPerson && (
							<NFormItemGi label="Gênero" path="gender">
								<NRadioGroup v-model:value={this.formData.gender}>
									<NRadio data-cy="input-gender-male" label="Masculino" value={Gender.MALE} />
									<NRadio data-cy="input-gender-female" label="Feminino" value={Gender.FEMALE} />
								</NRadioGroup>
							</NFormItemGi>
						)}

						{this.requestingLegalPerson && [
							<NFormItemGi label="CNPJ" path="cnpj">
								<AppInputCnpj data-cy="input-cnpj" v-model={this.formData.cnpj} />
							</NFormItemGi>,
						]}
					</NGrid>

					<ContactForm
						ref="formContactRef"
						v-model={this.formData.contact}
						renterId={(this.formData as any).id}
						originalEmail={this.initialEmail}
						originalPhone={this.initialPhone}
						forbiddenEmails={this.blockDuplicates.emails}
						forbiddenPhones={this.blockDuplicates.phones}
					/>
				</div>
			</NForm>
		);
	},
});

export default RenterForm;
